import { useState, useEffect } from "react"
import { Menu, X } from "lucide-react"
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import LanguageSwitcher from '../../components/LanguageSwitcher'

export default function LandingNavbar() {
  const { t, i18n } = useTranslation()
  const { user, loading: authLoading } = useAuth()
  const [isScrolled, setIsScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10)
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const navLinks = [
    { label: t('landing.nav.howItWorks'), href: "#how-it-works" },
    { label: t('landing.nav.features'), href: "#features" },
    { label: t('landing.nav.security'), href: "#security" },
    { label: t('landing.nav.pricing'), href: "#pricing" },
    { label: t('landing.nav.faq'), href: "#faq" },
  ]

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm"
          : "bg-white border-b border-gray-200"
      }`}
      aria-label={t('common.appName')}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14">
          <Link
            to={`/${i18n.language}`}
            className="flex items-center gap-2 font-bold text-gray-900 text-lg tracking-tight"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            <img src="/logo_v3.svg" alt="Sign This!" style={{ height: '2.75rem' }} />
          </Link>

          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="text-sm text-gray-600 hover:text-gray-900 transition-colors font-medium"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                {link.label}
              </a>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-2">
            <LanguageSwitcher
              className="w-9 h-9 text-gray-600 hover:text-gray-900 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors flex items-center justify-center"
              iconSize={16}
            />

            {!authLoading && user ? (
              <>
                <Link
                  to={`/${i18n.language}/create`}
                  className="text-sm font-semibold text-white bg-green-500 hover:bg-green-600 transition-colors px-4 py-1.5 rounded-md"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {t('landing.nav.startFree')}
                </Link>
                <Link
                  to={`/${i18n.language}/account`}
                  className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors px-3 py-1.5 border border-gray-200 rounded-md hover:bg-gray-50"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-green-500 text-white text-xs font-bold">
                    {user.displayName.charAt(0).toUpperCase()}
                  </span>
                  {user.displayName.split(' ')[0]}
                </Link>
              </>
            ) : (
              <>
                <Link
                  to={`/${i18n.language}/login`}
                  className="text-sm text-gray-600 hover:text-gray-900 transition-colors font-medium px-3 py-1.5"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {t('landing.nav.logIn')}
                </Link>
                <Link
                  to={`/${i18n.language}/create`}
                  className="text-sm font-semibold text-white bg-green-500 hover:bg-green-600 transition-colors px-4 py-1.5 rounded-md"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {t('landing.nav.startFree')}
                </Link>
              </>
            )}
          </div>

          <div className="md:hidden flex items-center gap-2">
            <LanguageSwitcher
              className="p-1.5 text-gray-600 border border-gray-200 rounded-md flex items-center justify-center w-8 h-8"
              iconSize={15}
            />
            <button
              className="p-2 text-gray-600 hover:text-gray-900"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-4 py-4 space-y-3">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              className="block text-sm text-gray-600 hover:text-gray-900 font-medium py-1"
              onClick={() => setMobileOpen(false)}
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {link.label}
            </a>
          ))}
          <div className="pt-2 border-t border-gray-100 flex flex-col gap-2">
            {!authLoading && user ? (
              <>
                <Link
                  to={`/${i18n.language}/create`}
                  className="text-sm font-semibold text-white bg-green-500 hover:bg-green-600 transition-colors px-4 py-2 rounded-md text-center"
                  onClick={() => setMobileOpen(false)}
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {t('landing.nav.startFree')}
                </Link>
                <Link
                  to={`/${i18n.language}/account`}
                  className="flex items-center gap-2 text-sm text-gray-600 font-medium py-1"
                  onClick={() => setMobileOpen(false)}
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-green-500 text-white text-xs font-bold">
                    {user.displayName.charAt(0).toUpperCase()}
                  </span>
                  {user.displayName}
                </Link>
              </>
            ) : (
              <>
                <Link
                  to={`/${i18n.language}/login`}
                  className="text-sm text-gray-600 font-medium py-1"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                  onClick={() => setMobileOpen(false)}
                >
                  {t('landing.nav.logIn')}
                </Link>
                <Link
                  to={`/${i18n.language}/create`}
                  className="text-sm font-semibold text-white bg-green-500 hover:bg-green-600 transition-colors px-4 py-2 rounded-md text-center"
                  onClick={() => setMobileOpen(false)}
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {t('landing.nav.startFree')}
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  )
}
